import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { _getCategories } from '@/api/address-book/queries';
import { _matchBusinessCard } from '@/api/contact-import/mutations';
import { hasOpenCategory } from '@/features/address-book/category-utils';
import { NO_OPEN_CATEGORY_REASON } from '@/features/address-book/locked-category';
import { useToast } from '@/shared/design-system';
import { useAddressBookCategoryScope } from '@/shared/modules/permissions';

import { createContact } from './api';
import { ModalShell } from './components/modal-shell';
import { CardUploadStep } from './steps/card-upload-step';
import { CategorySelectStep } from './steps/category-select-step';
import { ContactFormStep } from './steps/contact-form-step';
import { CreatingStep } from './steps/creating-step';
import { MatchCheckDrawer, NEW_CONTACT_VALUE } from './steps/match-check-drawer';
import { MethodSelectStep } from './steps/method-select-step';
import { QrScanStep } from './steps/qr-scan-step';
import { ReviewCardDrawer } from './steps/review-card-drawer';
import { buildCardMatchPayload } from './steps/review-card-drawer/use-card-match';
import { ReviewSaveStep } from './steps/review-save-step';
import { SuccessStep } from './steps/success-step';
import type { ContactData } from './types';
import { useAddContactFlow } from './use-add-contact-flow';

interface AddContactModalProps {
  open: boolean;
  onClose: () => void;
  initialCode?: string;
  presetCategoryIds?: string[];
  startWithManualEntry?: boolean;
  onContactCreated?: (_contactId: string) => void;
  canOpenCreatedContact?: boolean;
  onPickImport?: () => void;
  canImport?: boolean;
  canCreate?: boolean;
}

export function AddContactModal({
  open,
  onClose,
  initialCode,
  presetCategoryIds,
  startWithManualEntry = false,
  onContactCreated,
  canOpenCreatedContact = true,
  onPickImport,
  canImport = true,
  canCreate = true,
}: AddContactModalProps) {
  const { state, dispatch } = useAddContactFlow(
    initialCode,
    presetCategoryIds,
    startWithManualEntry,
  );
  const { data: categories = [], isLoading: isCategoriesLoading } = useQuery(_getCategories());
  const hasCategories = !isCategoriesLoading && categories.length > 0;
  // The server refuses to create a contact a caller without full view could not see, so for
  // that caller the category step is mandatory and only an open category satisfies it.
  const { isScoped } = useAddressBookCategoryScope();
  const skipCategories = !hasCategories && !isScoped;
  const noOpenCategoryReason =
    isScoped &&
    !isCategoriesLoading &&
    !hasOpenCategory(categories.flatMap((root) => root.children))
      ? NO_OPEN_CATEGORY_REASON
      : undefined;
  const [isCheckingMatch, setIsCheckingMatch] = useState(false);
  // Lives here, not in MatchCheckDrawer: that drawer unmounts on the way to categorySelect and
  // remounts on a failed create, which would silently reset the choice to the top candidate and
  // merge into the wrong contact on retry.
  const [matchSelection, setMatchSelection] = useState<string>(NEW_CONTACT_VALUE);
  const [categorySelection, setCategorySelection] = useState<string[] | null>(null);
  const firedForRef = useRef<string | null>(null);
  const { add: addToast } = useToast();

  useEffect(() => {
    if (state.step !== 'closed') {
      firedForRef.current = null;
      return;
    }
    const contactId = state.createdContactId ?? state.mergeContactId;
    if (contactId && firedForRef.current !== contactId) {
      firedForRef.current = contactId;
      onContactCreated?.(contactId);
    }
    onClose();
  }, [state.step, state.createdContactId, state.mergeContactId, onContactCreated, onClose]);

  const handleReviewContinue = async (data: Partial<ContactData>) => {
    setIsCheckingMatch(true);
    try {
      const result = await _matchBusinessCard(buildCardMatchPayload(data));
      const candidates = result.candidates ?? [];
      setMatchSelection(candidates[0]?.contactId ?? NEW_CONTACT_VALUE);
      dispatch({ type: 'REVIEW_CONTINUE', data, candidates, skipCategories });
    } catch {
      // Deliberately fail CLOSED. Continuing with an empty candidate list would let the user create
      // a contact the check might have matched, with no verdicts behind it — the duplicate this
      // screen exists to prevent. The drawer stays open with everything the user typed, so the
      // Continue button is itself the retry.
      addToast({
        title: 'Could not check for existing contacts',
        description: 'Nothing was saved. Check your connection and press Continue again.',
        data: { status: 'error' },
      });
    } finally {
      setIsCheckingMatch(false);
    }
  };

  useEffect(() => {
    if (open) return;
    dispatch({ type: 'RESET', startWithManualEntry });
    setCategorySelection(null);
  }, [open, dispatch, startWithManualEntry]);

  useEffect(() => {
    dispatch({ type: 'PRESET_CATEGORIES', ids: presetCategoryIds ?? [] });
  }, [presetCategoryIds, dispatch]);

  const header = (() => {
    switch (state.step) {
      case 'methodSelect':
        return {
          title: 'Add Contact',
          description: 'Enter contact information to get started',
        };
      case 'qrScan':
        return {
          title: 'Enter the User Code',
          description:
            "QR code scanning is only available on mobile. On desktop, enter the 6-digit code shown alongside your contact's QR code.",
        };
      case 'cardUpload':
        return {
          title: 'Upload business card',
          description: 'Upload a business card to automatically create a contact',
        };
      default:
        return undefined;
    }
  })();

  // The drawer steps below render on their own, outside ModalShell. A flow that starts on one
  // (startWithManualEntry) is already on it while closed, and some hosts are always mounted,
  // so they only show while the flow is open.
  const shownStep = open ? state.step : null;

  const view = (() => {
    switch (state.step) {
      case 'methodSelect':
        return (
          <MethodSelectStep
            onPickQr={() => dispatch({ type: 'PICK_QR' })}
            onPickCard={isScoped ? undefined : () => dispatch({ type: 'PICK_CARD' })}
            onPickManual={() => dispatch({ type: 'PICK_MANUAL' })}
            onPickImport={onPickImport}
            canImport={canImport}
            canCreate={canCreate}
          />
        );
      case 'qrScan':
        return (
          <QrScanStep
            initialCode={state.pendingCode}
            onCodeConsumed={() => dispatch({ type: 'CODE_CONSUMED' })}
            onScanned={(data, referenceId, contactCode) =>
              dispatch({
                type: 'SCANNED',
                data,
                referenceId,
                contactCode,
                chooseCategories: isScoped,
              })
            }
            onBack={() => dispatch({ type: 'BACK' })}
          />
        );
      case 'cardUpload':
        return (
          <CardUploadStep
            onExtractOk={(data) => dispatch({ type: 'EXTRACT_OK', data })}
            onBack={() => dispatch({ type: 'BACK' })}
          />
        );
      default:
        return null;
    }
  })();

  return (
    <>
      <ModalShell
        open={open && view !== null}
        onClose={onClose}
        title={header?.title}
        description={header?.description}
      >
        {view}
      </ModalShell>

      {shownStep === 'contactForm' && (
        <ContactFormStep
          key="contactForm"
          defaults={state.data}
          submitLabel={skipCategories ? 'Add Contact' : 'Continue'}
          submitBlockedReason={noOpenCategoryReason}
          onSubmit={(data) =>
            skipCategories
              ? dispatch({ type: 'SUBMIT_DIRECT', data })
              : dispatch({ type: 'SUBMIT', data })
          }
          onCreateOrg={
            isScoped
              ? undefined
              : (name, formSnapshot) => dispatch({ type: 'CREATE_ORG', name, formSnapshot })
          }
          onBack={() => dispatch({ type: 'BACK' })}
          onClose={onClose}
        />
      )}

      {shownStep === 'orgCreate' && (
        <ContactFormStep
          key="orgCreate"
          defaults={{ name: state.orgCreateName }}
          isOrgSubForm
          submitLabel="Create Organization"
          onSubmit={async (data) => {
            const result = await createContact({ ...data, categoryIds: [] });
            dispatch({ type: 'ORG_CREATED', orgId: result.id, orgName: data.name ?? '' });
          }}
          onBack={() => dispatch({ type: 'BACK' })}
          onClose={onClose}
        />
      )}

      {shownStep === 'categorySelect' && (
        <CategorySelectStep
          contactName={state.data.name}
          selectedIds={categorySelection ?? state.categoryIds}
          requireOpenCategory={isScoped}
          onSelectedIdsChange={setCategorySelection}
          onFinish={(ids) => dispatch({ type: 'FINISH_CATEGORIES', ids })}
          onBack={() => dispatch({ type: 'BACK' })}
          onClose={onClose}
        />
      )}

      {shownStep === 'reviewSave' && (
        <ReviewSaveStep
          data={state.data}
          categoryIds={state.categoryIds}
          canEditCategories={!skipCategories}
          onSave={(notes) => dispatch({ type: 'SAVE_REVIEWED', notes })}
          onEditCategories={() => dispatch({ type: 'EDIT_CATEGORIES' })}
          onBack={() => dispatch({ type: 'BACK' })}
          onClose={onClose}
        />
      )}

      {shownStep === 'creating' && <CreatingStep />}

      {shownStep === 'success' && (
        <SuccessStep
          id={state.createdContactId}
          isNew={state.createdContactIsNew}
          canOpenContact={canOpenCreatedContact}
          onFinish={() => dispatch({ type: 'FINISH' })}
        />
      )}

      {shownStep === 'reviewCard' && (
        <ReviewCardDrawer
          open
          defaults={state.data}
          isSubmitting={isCheckingMatch}
          onContinue={handleReviewContinue}
          onBack={() => dispatch({ type: 'BACK' })}
          onClose={onClose}
        />
      )}

      {(shownStep === 'matchCheck' || shownStep === 'merging') && (
        <MatchCheckDrawer
          open
          data={state.data}
          candidates={state.matchCandidates ?? []}
          sourceLabel="Business card"
          isSubmitting={state.step === 'merging'}
          selection={matchSelection}
          onSelectionChange={setMatchSelection}
          onContinue={(contactId) =>
            dispatch({ type: 'MATCH_RESOLVED', contactId, skipCategories })
          }
          onBack={() => dispatch({ type: 'BACK' })}
          onClose={onClose}
        />
      )}
    </>
  );
}
